import { DayOfWeek, LocalDate, Period, TemporalAdjusters, YearMonth } from '@js-joda/core';
import { compact, flatMap, forEach, isEmpty, map, max, min, orderBy } from 'es-toolkit/compat';
import { core } from '@tock/coretypes';
import { oc } from 'ts-optchain';

// This is mirrored logic from the ScheduleResolver classes in core
// Please do not make changes without also considering those classes.

export function resolveSchedule(schedule?: core.ISchedule): LocalDate[] {
  const resolvedDates: LocalDate[] = [];
  if (!schedule) {
    return resolvedDates;
  }
  resolveDiscreteDates(schedule).map((d) => resolvedDates.push(d));
  if (!isEmpty(schedule.repetition)) {
    flatMap(schedule.repetition, (repetition) => resolveScheduleRepetition(repetition)).map((d) =>
      resolvedDates.push(d)
    );
  }
  return orderBy(dedupeLocalDates(resolvedDates), (d: LocalDate) => d.toEpochDay());
}

export function resolveDiscreteDates(schedule: core.ISchedule): LocalDate[] {
  const scheduleDates = oc(schedule).date([]);
  return dedupeLocalDates(
    compact(
      scheduleDates.map((scheduleDate: core.IScheduleDate) =>
        scheduleDate.date ? LocalDate.parse(scheduleDate.date) : undefined
      )
    )
  );
}

export function resolveScheduleRepetition(repetition: core.IScheduleRepetition): LocalDate[] {
  const { repeatsFromDate, repeatsUntilDate, repeatsUntilNDays, repeatsOn } = repetition;
  if ((repeatsFromDate == null) || ((repeatsUntilDate == null) && (repeatsUntilNDays == null))) {
    return [];
  }

  const from = LocalDate.parse(repeatsFromDate);
  const until = getRepeatsUntil(
    LocalDate.now(), // add business zone
    from,
    repeatsUntilDate ? LocalDate.parse(repeatsUntilDate) : undefined,
    repeatsUntilNDays ?? undefined
  );
  if (!until) {
    return [];
  }

  switch (repetition.repeatsEvery) {
    case 'DAY':
      return resolveDailyScheduleRepetition(from, until);
    case 'WEEK':
      if (!repeatsOn) {
        return [];
      }
      return resolveWeeklyScheduleRepetition(from, until, repeatsOn);
    case 'MONTH':
      if (!repeatsOn) {
        return [];
      }
      return resolveMonthlyScheduleRepetition(from, until, repeatsOn);
    default:
      return [];
  }
}
export function resolveDailyScheduleRepetition(from: LocalDate, until: LocalDate): LocalDate[] {
  if (from.isAfter(until)) {
    return [];
  }
  const resolvedDates: LocalDate[] = [];
  for (let i = from.toEpochDay(); i <= until.toEpochDay(); i++) {
    resolvedDates.push(LocalDate.ofEpochDay(i));
  }
  return dedupeLocalDates(resolvedDates);
}

export function minDate(dates: LocalDate[]): LocalDate | undefined {
  const minEpochDay = min(map(dates, (date) => date.toEpochDay()));
  return minEpochDay ? LocalDate.ofEpochDay(minEpochDay) : undefined;
}

export function maxDate(dates: LocalDate[]): LocalDate | undefined {
  const minEpochDay = max(map(dates, (date) => date.toEpochDay()));
  return minEpochDay ? LocalDate.ofEpochDay(minEpochDay) : undefined;
}

export function resolveWeeklyScheduleRepetition(
  from: LocalDate,
  until: LocalDate,
  repeatsOn: core.IRepetitionOccurrenceSet
): LocalDate[] {
  const resolvedDates: LocalDate[] = [];

  const daysBetween = until.toEpochDay() - from.toEpochDay();
  const weeksToCheck = Math.floor(daysBetween / 7) + 1;
  for (let weekOffset = 0; weekOffset < weeksToCheck; weekOffset++) {
    const startOfWeekToCheck = from.plusWeeks(weekOffset);
    oc(repeatsOn)
      .nthWeekday([])
      .map((nthWeekday) => {
        if (!nthWeekday.weekday) {
          return;
        }
        const proposedNthWeekday: LocalDate = startOfWeekToCheck.with(
          TemporalAdjusters.nextOrSame(DayOfWeek[nthWeekday.weekday])
        );
        if (isInRange(proposedNthWeekday, from, until)) {
          resolvedDates.push(proposedNthWeekday);
        }
      });
  }

  return dedupeLocalDates(resolvedDates);
}

export function resolveMonthlyScheduleRepetition(
  from: LocalDate,
  until: LocalDate,
  repeatsOn: core.IRepetitionOccurrenceSet
): LocalDate[] {
  const resolvedDates: LocalDate[] = [];
  const monthsToCheck = Period.between(from, until).toTotalMonths() + 1;
  for (let monthOffset = 0; monthOffset < monthsToCheck; monthOffset++) {
    const referenceDate = from.plusMonths(monthOffset);

    //Nth Days
    oc(repeatsOn)
      .nthDay([])
      .map((nthDay) => {
        if (!nthDay.n) {
          return;
        }
        if (YearMonth.of(referenceDate.year(), referenceDate.month()).lengthOfMonth() < nthDay.n) {
          return;
        }
        const proposedNthDate: LocalDate = referenceDate.withDayOfMonth(nthDay.n);
        if (isInRange(proposedNthDate, from, until)) {
          resolvedDates.push(proposedNthDate);
        }
      });

    //Nth Weekdays
    oc(repeatsOn)
      .nthWeekday([])
      .map((nthWeekday) => {
        if (!nthWeekday.weekday || !nthWeekday.n || nthWeekday.n > 5) {
          return;
        }
        const proposedNthWeekday: LocalDate = referenceDate.with(
          TemporalAdjusters.dayOfWeekInMonth(nthWeekday.n, DayOfWeek[nthWeekday.weekday])
        );
        if (
          isInRange(proposedNthWeekday, from, until) &&
          proposedNthWeekday.month() === referenceDate.month()
        ) {
          resolvedDates.push(proposedNthWeekday);
        }
      });
  }
  return dedupeLocalDates(resolvedDates);
}

function getRepeatsUntil(
  now: LocalDate,
  repeatsFromDate?: LocalDate,
  repeatsUntilDate?: LocalDate,
  repeatsUntilNDays?: number
): LocalDate | undefined {
  const hasTermination = (repeatsUntilDate != null) || (repeatsUntilNDays != null);
  if ((repeatsFromDate == null) || !hasTermination) {
    return undefined;
  }

  const repeatsUntilRollingDate = getRepeatsUntilRollingDate(
    now,
    repeatsFromDate,
    repeatsUntilNDays
  );

  if (repeatsUntilRollingDate == null) {
    return repeatsUntilDate;
  } else if (repeatsUntilDate == null) {
    return repeatsUntilRollingDate;
  }

  return repeatsUntilDate.isBefore(repeatsUntilRollingDate)
    ? repeatsUntilDate
    : repeatsUntilRollingDate;
}

function getRepeatsUntilRollingDate(
  now: LocalDate,
  repeatsFromDate?: LocalDate,
  repeatsUntilNDays?: number
): LocalDate | undefined {
  if ((repeatsFromDate == null) || (repeatsUntilNDays == null) || Number.isNaN(repeatsUntilNDays)) {
    return undefined;
  }
  return (repeatsFromDate.isAfter(now) ? repeatsFromDate : now).plusDays(repeatsUntilNDays);
}

// inclusive check of date being in range
function isInRange(proposedDate: LocalDate, from: LocalDate, until: LocalDate) {
  const isAfterOrEqualToFrom = proposedDate.isAfter(from) || proposedDate.isEqual(from);
  const isBeforeOrEqualToUntil = proposedDate.isBefore(until) || proposedDate.isEqual(until);
  return isAfterOrEqualToFrom && isBeforeOrEqualToUntil;
}

function dedupeLocalDates(localDates: LocalDate[]) {
  const resolvedDates: { [key: string]: boolean } = {};
  forEach(localDates, (d) => {
    resolvedDates[d.toString()] = true;
  });
  return Object.keys(resolvedDates).map((dateString) => LocalDate.parse(dateString));
}
